"use client";

import type React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import Image from "next/image";
import noImage from "@/public/ecomsaas-assets/images/no-image.webp";
import { getAssetsBaseUrl } from "@/services/storeService";

interface MediaItem {
  id: string;
  type: "image" | "video";
  src: string;
  alt: string;
}

interface ImageGallerySwiperProps {
  imageUrls: MediaItem[];
}

 const assets = `${getAssetsBaseUrl()}`

export default function ImageGallerySwiper({ imageUrls }: ImageGallerySwiperProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  // Mouse drag states
  const [mouseDown, setMouseDown] = useState(false);
  const [mouseStart, setMouseStart] = useState(0);
  const [mouseEnd, setMouseEnd] = useState(0);

  // Autoplay state - always true
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const mobileThumbnailRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const mobileThumbnailContainerRef = useRef<HTMLDivElement>(null);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);

  const validImageUrls = Array.isArray(imageUrls) ? imageUrls : [];

  const goToSlide = (index: number) => {
    if (index >= 0 && index < validImageUrls.length) {
      setCurrentIndex(index);
    }
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + validImageUrls.length) % validImageUrls.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % validImageUrls.length);
  };

  /* ---------- SCROLL ACTIVE THUMBNAIL INTO VIEW (Desktop & Mobile) - FIXED VERSION ---------- */
  useEffect(() => {
    // Desktop thumbnails (vertical scroll) - Manual scroll control
    const activeThumbnail = thumbnailRefs.current[currentIndex];
    const thumbnailContainer = thumbnailContainerRef.current;

    if (activeThumbnail && thumbnailContainer) {
      const containerRect = thumbnailContainer.getBoundingClientRect();
      const thumbnailRect = activeThumbnail.getBoundingClientRect();

      // Calculate relative positions
      const thumbnailTop = activeThumbnail.offsetTop;
      const thumbnailHeight = thumbnailRect.height;
      const containerHeight = containerRect.height;
      const currentScroll = thumbnailContainer.scrollTop;

      // Check if thumbnail is not fully visible
      const isAboveView = thumbnailTop < currentScroll;
      const isBelowView = thumbnailTop + thumbnailHeight > currentScroll + containerHeight;

      if (isAboveView) {
        // Scroll to show at top
        thumbnailContainer.scrollTo({
          top: thumbnailTop - 10, // 10px padding from top
          behavior: 'smooth'
        });
      } else if (isBelowView) {
        // Scroll to show at bottom
        thumbnailContainer.scrollTo({
          top: thumbnailTop - containerHeight + thumbnailHeight + 10, // 10px padding from bottom
          behavior: 'smooth'
        });
      }
    }

    // Mobile thumbnails (horizontal scroll) - Manual scroll control
    const activeMobileThumbnail = mobileThumbnailRefs.current[currentIndex];
    const mobileThumbnailContainer = mobileThumbnailContainerRef.current;

    if (activeMobileThumbnail && mobileThumbnailContainer) {
      const containerRect = mobileThumbnailContainer.getBoundingClientRect();
      const thumbnailRect = activeMobileThumbnail.getBoundingClientRect();

      // Calculate relative positions
      const thumbnailLeft = activeMobileThumbnail.offsetLeft;
      const thumbnailWidth = thumbnailRect.width;
      const containerWidth = containerRect.width;
      const currentScroll = mobileThumbnailContainer.scrollLeft;

      // Check if thumbnail is not fully visible
      const isLeftOfView = thumbnailLeft < currentScroll;
      const isRightOfView = thumbnailLeft + thumbnailWidth > currentScroll + containerWidth;

      if (isLeftOfView) {
        // Scroll to show at left
        mobileThumbnailContainer.scrollTo({
          left: thumbnailLeft - 16, // 16px padding from left
          behavior: 'smooth'
        });
      } else if (isRightOfView) {
        // Scroll to show at right
        mobileThumbnailContainer.scrollTo({
          left: thumbnailLeft - containerWidth + thumbnailWidth + 16, // 16px padding from right
          behavior: 'smooth'
        });
      }
    }
  }, [currentIndex]);

  /* ---------- VIDEO AUTOPLAY AND END DETECTION ---------- */
  useEffect(() => {
    const currentItem = validImageUrls[currentIndex];
    const currentVideo = videoRefs.current[currentIndex];

    if (currentItem?.type === "video" && currentVideo) {
      setIsVideoPlaying(true);

      // Play the video
      currentVideo.play().catch(() => { });

      // Listen for video end event
      const handleVideoEnd = () => {
        setIsVideoPlaying(false);

        // Move to next slide after video ends
        setTimeout(() => {
          goToNext();
        }, 300);
      };

      currentVideo.addEventListener('ended', handleVideoEnd);

      return () => {
        currentVideo.removeEventListener('ended', handleVideoEnd);
        currentVideo.pause();
      };
    } else {
      setIsVideoPlaying(false);

      // Pause other videos
      videoRefs.current.forEach((v, i) => {
        if (v && i !== currentIndex) {
          v.pause();
          v.currentTime = 0;
        }
      });
    }
  }, [currentIndex, validImageUrls]);

  /* ---------- AUTOPLAY LOGIC - ALWAYS ON (2 seconds) ---------- */
  useEffect(() => {
    // Clear any existing interval
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }

    // Don't autoplay if video is playing or only 1 image
    if (isVideoPlaying || validImageUrls.length <= 1) {
      return;
    }

    // Always start autoplay
    autoPlayRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % validImageUrls.length);
    }, 2000);

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isVideoPlaying, validImageUrls.length]);

  /* ---------- TOUCH HANDLERS (Mobile) ---------- */
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isSwipe = Math.abs(distance) > 50;

    if (isSwipe) {
      if (distance > 0) {
        goToNext();
      } else {
        goToPrevious();
      }
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  /* ---------- MOUSE HANDLERS (Desktop) ---------- */
  const handleMouseDown = (e: React.MouseEvent) => {
    setMouseDown(true);
    setMouseStart(e.pageX);
    if (containerRef.current) {
      containerRef.current.style.cursor = 'grabbing';
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!mouseDown) return;
    e.preventDefault();
    setMouseEnd(e.pageX);
  };

  const handleMouseUp = () => {
    if (!mouseDown) return;

    if (containerRef.current) {
      containerRef.current.style.cursor = 'grab';
    }

    if (!mouseStart || !mouseEnd) {
      setMouseDown(false);
      return;
    }

    const distance = mouseStart - mouseEnd;
    const isSwipe = Math.abs(distance) > 50;

    if (isSwipe) {
      if (distance > 0) {
        goToNext();
      } else {
        goToPrevious();
      }
    }

    setMouseDown(false);
    setMouseStart(0);
    setMouseEnd(0);
  };

  const handleMouseLeave = () => {
    if (mouseDown && containerRef.current) {
      containerRef.current.style.cursor = 'grab';
    }
    setMouseDown(false);
    setMouseStart(0);
    setMouseEnd(0);
  };

  /* ---------- PRELOAD IMAGES ---------- */
  useEffect(() => {
    const next = (currentIndex + 1) % validImageUrls.length;
    const prev = (currentIndex - 1 + validImageUrls.length) % validImageUrls.length;

    const preload = (item: MediaItem) => {
      if (item.type === "image") {
        const img = new window.Image();
        img.src = `${assets}${item.src}`;
      }
    };

    if (validImageUrls[next]) preload(validImageUrls[next]);
    if (validImageUrls[prev]) preload(validImageUrls[prev]);
  }, [currentIndex, validImageUrls]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, []);

  if (validImageUrls.length === 0) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 py-8 text-center">
        <p className="text-gray-600">No media available</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-0 sm:px-4 py-2 sm:py-4 lg:py-8 overflow-hidden">
      <div className="flex flex-col lg:flex-row gap-3 lg:gap-4 w-full">
        {/* ---------- LEFT THUMBNAILS WITH SCROLL (Desktop Only) ---------- */}
        <div
          ref={thumbnailContainerRef}
          className="hidden lg:flex flex-col gap-y-3 flex-shrink-0 p-2 max-h-[520px] overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 pr-2"
        >
          {validImageUrls.map((item, idx) => (
            <button
              key={item.id}
              ref={(el) => {
                thumbnailRefs.current[idx] = el;
              }}
              onClick={() => goToSlide(idx)}
              className={`relative w-[100px] h-[130px] rounded-lg overflow-hidden transition-all duration-300 border-2 flex-shrink-0 ${idx === currentIndex
                ? "border-primary border-4 scale-105"
                : "border-gray-200 hover:border-gray-400"
                }`}
              aria-label={`View image ${idx + 1}`}
            >
              {item.type === "image" ? (
                <Image
                  src={`${assets}${item.src}`}
                  alt={item.alt}
                  fill
                  className="object-cover"
                  sizes="100px"
                  onError={(e: any) => {
                    e.currentTarget.src = noImage.src;
                  }}
                  unoptimized={process.env.NODE_ENV === 'development'}
                />
              ) : (
                <div className="relative w-full h-full">
                  <video
                    src={`${assets}${item.src}`}
                    className="w-full h-full object-cover"
                    muted
                    preload="metadata"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                    </svg>
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* ---------- MAIN IMAGE DISPLAY ---------- */}
        <div className="flex-1 w-full min-w-0">
          <div
            ref={containerRef}
            className="relative w-full aspect-[3/4] lg:aspect-[4/5] bg-gray-50 rounded-none sm:rounded-lg overflow-hidden cursor-grab select-none"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            {/* Slides Container */}
            <div className="relative w-full h-full">
              {validImageUrls.map((item, idx) => {
                const isActive = idx === currentIndex;
                const isPrev = idx === currentIndex - 1;
                const isNext = idx === currentIndex + 1;
                const shouldRender = isActive || isPrev || isNext;

                if (!shouldRender) return null;

                let translateX = 0;
                if (isPrev) translateX = -100;
                if (isNext) translateX = 100;

                return (
                  <div
                    key={item.id}
                    className={`absolute inset-0 w-full h-full transition-all duration-500 ease-out ${isActive ? "opacity-100 z-10" : "opacity-0 z-0"
                      }`}
                    style={{
                      transform: `translateX(${translateX}%)`,
                    }}
                  >
                    {item.type === "video" ? (
                      <video
                        ref={(el) => {
                          if (videoRefs.current) {
                            videoRefs.current[idx] = el;
                          }
                        }}
                        src={`${assets}${item.src}`}
                        className="w-full h-full object-cover"
                        controls
                        loop={false}
                        muted={false}
                        preload="auto"
                        playsInline
                      />
                    ) : (
                      <div className="relative w-full h-full">
                        <Image
                          src={`${assets}${item.src}`}
                          alt={item.alt}
                          fill
                          className="object-cover pointer-events-none"
                          priority={idx === 0}
                          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 75vw"
                          onError={(e: any) => {
                            e.currentTarget.src = noImage.src;
                          }}
                          draggable={false}
                          unoptimized={process.env.NODE_ENV === 'development'}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Image Counter */}
            {validImageUrls.length > 1 && (
              <div className="absolute bottom-2 right-2 sm:bottom-4 sm:right-4 bg-black/60 text-white px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm font-medium z-20 pointer-events-none">
                {currentIndex + 1} / {validImageUrls.length}
              </div>
            )}

            {/* Swipe Indicator Dots */}
            {validImageUrls.length > 1 && (
              <div className="absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 sm:gap-2 z-20">
                {validImageUrls.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => goToSlide(idx)}
                    className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-all duration-300 ${idx === currentIndex
                      ? "bg-white w-4 sm:w-6"
                      : "bg-white/50 hover:bg-white/75"
                      }`}
                    aria-label={`Go to slide ${idx + 1}`}
                  />
                ))}
              </div>
            )}

            {/* Autoplay indicator */}
            {!isVideoPlaying && validImageUrls.length > 1 && (
              <div className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-black/40 text-white p-1.5 sm:p-2 rounded-full z-20 pointer-events-none">
                <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                </svg>
              </div>
            )}
          </div>

          {/* ---------- MOBILE THUMBNAILS WITH AUTO SCROLL ---------- */}
          {validImageUrls.length > 1 && (
            <div
              ref={mobileThumbnailContainerRef}
              className="lg:hidden mt-3 sm:mt-4 flex gap-2 p-2 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent px-4 sm:px-0 scroll-smooth"
            >
              {validImageUrls.map((item, idx) => (
                <button
                  key={item.id}
                  ref={(el) => {
                    mobileThumbnailRefs.current[idx] = el;
                  }}
                  onClick={() => goToSlide(idx)}
                  className={`relative flex-shrink-0 w-16 h-20 sm:w-20 sm:h-24 rounded-lg overflow-hidden transition-all duration-300 border-2 snap-center ${idx === currentIndex
                    ? "border-primary border-4 scale-105"
                    : "border-gray-200"
                    }`}
                  aria-label={`View image ${idx + 1}`}
                >
                  {item.type === "image" ? (
                    <Image
                      src={`${assets}${item.src}`}
                      alt={item.alt}
                      fill
                      className="object-cover"
                      sizes="80px"
                      onError={(e: any) => {
                        e.currentTarget.src = noImage.src;
                      }}
                      unoptimized={process.env.NODE_ENV === 'development'}
                    />
                  ) : (
                    <div className="relative w-full h-full">
                      <video
                        src={`${assets}${item.src}`}
                        className="w-full h-full object-cover"
                        muted
                        preload="metadata"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                        </svg>
                      </div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Custom CSS for scrollbar styling */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-thin::-webkit-scrollbar {
          width: 6px;
          height: 4px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: transparent;
          border-radius: 10px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: #9ca3af;
          border-radius: 10px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: #6b7280;
        }
        .scroll-smooth {
          scroll-behavior: smooth;
        }
      `}</style>
    </div>
  );
}
